import { MediaPicker } from '@/components/media/MediaPicker';
import type { MediaRow } from '@/routes/media/types';
import { Button, Chip, Input, Spinner } from '@sjuni/ui';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { FormError } from '@/components/FormError';
import { PublishingSidebar } from '@/components/editor/PublishingSidebar';
import { RichTextField } from '@/components/editor/RichTextField';
import { SlugField } from '@/components/form/SlugField';
import { TranslatableListField } from '@/components/form/TranslatableListField';
import { TranslatableField } from '@/components/form/TranslatableField';
import { TranslationProgress } from '@/components/form/TranslationProgress';
import { type Column, ResourceListScreen } from '@/components/list/ResourceListScreen';
import { useAuth } from '@/lib/auth/useAuth';
import { useEditorLocale } from '@/lib/editorLocale/useEditorLocale';
import type { TranslatedValue } from '@/lib/forms/translatable';
import { listToInput, valueFor } from '@/lib/forms/translatable';
import { useListParams } from '@/lib/list/useListParams';
import { useResourceForm } from '@/lib/forms/useResourceForm';

import type { ProjectInput, ProjectRow } from './types';

/**
 * docs/05-ADMIN-DASHBOARD.md §2 — `/projects`.
 *
 * The research projects CLAUDE.md lists by name: PRODIGE, ERIC-GE, CIF-EEPIIQ,
 * ERASMUS+, DITECH, VETPRO, ETHICS, Horizon Europe. Those acronyms are how
 * everybody refers to them, which is why `name` is a plain string rather than a
 * translatable field — see `ProjectRequest`.
 */
export function ProjectsPage() {
  const rest = (useParams()['*'] ?? '').replace(/\/+$/, '');

  return rest === '' ? <List /> : <Editor recordId={rest} />;
}

function List() {
  const { t } = useTranslation();
  const { locale } = useEditorLocale();
  const list = useListParams('sort_order');

  const columns: Column<ProjectRow>[] = [
    {
      key: 'name',
      label: t('content.projects.columns.name'),
      render: (row) => (
        <div className="min-w-48">
          {/* The acronym leads: it is the handle, and the title is the gloss. */}
          <span className="font-extrabold text-primary">{row.name}</span>
          <span className="mt-0.5 block text-caption text-secondary">
            {valueFor(row.title, locale)}
          </span>
        </div>
      ),
    },
    {
      key: 'programme',
      label: t('content.projects.columns.programme'),
      render: (row) => <span className="text-secondary">{row.programme ?? '—'}</span>,
    },
    {
      key: 'period',
      label: t('content.projects.columns.period'),
      render: (row) => {
        const running = row.ends_on === null || new Date(row.ends_on) >= new Date();

        return (
          <span className="text-caption text-secondary">
            {row.starts_on ? new Date(row.starts_on).getFullYear() : '—'}
            {' – '}
            {/* An open-ended project is running, not finished. §11 groups on
                exactly this, and treating a null as "over" would quietly
                retire the longest-lived programmes the university has. */}
            {row.ends_on ? new Date(row.ends_on).getFullYear() : t('content.projects.ongoing')}
            {running ? null : ` · ${t('content.projects.completed')}`}
          </span>
        );
      },
    },
    {
      key: 'status',
      label: t('content.columns.status'),
      render: (row) => (
        <Chip tone={row.status === 'published' ? 'navy' : 'gold'}>
          {t(`editor.publishing.statuses.${row.status}`)}
        </Chip>
      ),
    },
    {
      key: 'sort_order',
      label: t('content.fields.order'),
      sortable: true,
      align: 'end',
      render: (row) => row.sort_order,
    },
  ];

  return (
    <ResourceListScreen
      resource="projects"
      title={t('nav.items.projects')}
      intro={t('content.projects.intro')}
      columns={columns}
      labelFor={(row) => row.name}
      defaultSort="sort_order"
      emptyTitle={t('content.projects.empty.title')}
      emptyBody={t('content.projects.empty.body')}
      newLabel={t('content.projects.new')}
      deleteTitle={t('content.projects.delete.title')}
      deleteBody={(label) => t('content.delete.body', { label })}
      filters={
        <Button
          type="button"
          size="sm"
          variant={list.params.active === '1' ? 'primary' : 'secondary'}
          aria-pressed={list.params.active === '1'}
          onClick={() => list.setFilter('active', list.params.active === '1' ? null : '1')}
        >
          {t('content.projects.onlyRunning')}
        </Button>
      }
    />
  );
}

interface Fields {
  name: string;
  title: TranslatedValue;
  slug: TranslatedValue;
  summary: TranslatedValue;
  body: TranslatedValue;
  funder: TranslatedValue;
  partners: Record<string, string[] | null>;
  programme: string;
  starts_on: string;
  ends_on: string;
  budget: string;
  coordinator_staff_id: string;
  logo_media_id: number | null;
  logo: MediaRow | null;
  cover_media_id: number | null;
  cover: MediaRow | null;
  website_url: string;
  sort_order: number;
  status: ProjectRow['status'];
  published_at: string | null;
  pending_review: boolean;
}

function Editor({ recordId }: { recordId: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { can } = useAuth();

  const mayPublish = can('projects.publish');

  const { form, values, record, isNew, isLoading, isSubmitting, formError, submit, guard } =
    useResourceForm<ProjectRow, Fields, ProjectInput>({
      resource: 'projects',
      recordId,
      empty,
      toFields,
      toInput: (fields) => toInput(fields, mayPublish),
      errorFields: [
        'name',
        'title',
        'slug',
        'summary',
        'body',
        'funder',
        'programme',
        'starts_on',
        'ends_on',
        'budget',
        'coordinator_staff_id',
        'website_url',
        'sort_order',
        'status',
        'published_at',
      ],
    });

  const { register, setValue, formState } = form;

  if (isLoading) {
    return (
      <div className="grid min-h-96 place-items-center">
        <Spinner size="lg" label={t('common.loading')} />
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="mx-auto max-w-5xl">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <h1 className="text-h2 font-extrabold text-primary">
          {isNew ? t('content.projects.new') : t('content.projects.edit')}
        </h1>

        <Button type="button" variant="ghost" size="sm" onClick={() => navigate('/projects')}>
          {t('common.back')}
        </Button>
      </div>

      <FormError message={formError} />

      <div className="grid gap-8 lg:grid-cols-[1fr_280px]">
        <div className="space-y-5">
          <Input
            {...register('name')}
            label={t('content.projects.fields.name')}
            hint={t('content.projects.fields.nameHint')}
            error={formState.errors.name?.message}
          />

          <TranslatableField
            label={t('content.fields.title')}
            name="title"
            value={values.title}
            onChange={(next) => setValue('title', next, { shouldDirty: true })}
            error={formState.errors.title?.message}
          />

          <SlugField
            value={values.slug}
            onChange={(next) => setValue('slug', next, { shouldDirty: true })}
            original={record?.slug}
            published={values.status === 'published'}
            error={formState.errors.slug?.message}
          />

          <TranslatableField
            label={t('content.fields.summary')}
            name="summary"
            multiline
            rows={3}
            value={values.summary}
            onChange={(next) => setValue('summary', next, { shouldDirty: true })}
            required={false}
          />

          <RichTextField
            label={t('content.fields.body')}
            value={values.body}
            onChange={(next) => setValue('body', next, { shouldDirty: true })}
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <TranslatableField
              label={t('content.projects.fields.funder')}
              hint={t('content.projects.fields.funderHint')}
              name="funder"
              value={values.funder}
              onChange={(next) => setValue('funder', next, { shouldDirty: true })}
              required={false}
            />

            <Input
              {...register('programme')}
              label={t('content.projects.fields.programme')}
              hint={t('content.projects.fields.programmeHint')}
              error={formState.errors.programme?.message}
            />
          </div>

          <TranslatableListField
            label={t('content.projects.fields.partners')}
            hint={t('content.projects.fields.partnersHint')}
            value={values.partners}
            onChange={(next) => setValue('partners', next, { shouldDirty: true })}
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              {...register('starts_on')}
              type="date"
              label={t('content.projects.fields.starts')}
              error={formState.errors.starts_on?.message}
            />

            <Input
              {...register('ends_on')}
              type="date"
              label={t('content.projects.fields.ends')}
              hint={t('content.projects.fields.endsHint')}
              error={formState.errors.ends_on?.message}
            />

            <Input
              {...register('budget')}
              type="number"
              min={0}
              step="0.01"
              label={t('content.projects.fields.budget')}
              error={formState.errors.budget?.message}
            />

            <Input
              {...register('coordinator_staff_id')}
              type="number"
              min={1}
              label={t('content.projects.fields.coordinator')}
              hint={t('content.projects.fields.coordinatorHint')}
              error={formState.errors.coordinator_staff_id?.message}
            />

            <Input
              {...register('website_url')}
              type="url"
              label={t('content.projects.fields.website')}
              error={formState.errors.website_url?.message}
            />

            <MediaPicker
              label={t('content.projects.fields.logo')}
              hint={t('content.fields.coverHint')}
              value={values.logo}
              onChange={(mediaId, media) => {
                setValue('logo_media_id', mediaId, { shouldDirty: true });
                setValue('logo', media, { shouldDirty: true });
              }}
              error={formState.errors.logo_media_id?.message}
            />

            <MediaPicker
              label={t('content.fields.cover')}
              hint={t('content.fields.coverHint')}
              value={values.cover}
              onChange={(mediaId, media) => {
                setValue('cover_media_id', mediaId, { shouldDirty: true });
                setValue('cover', media, { shouldDirty: true });
              }}
              error={formState.errors.cover_media_id?.message}
            />

            <Input
              {...register('sort_order', { valueAsNumber: true })}
              type="number"
              min={0}
              label={t('content.fields.order')}
              hint={t('content.fields.orderHint')}
            />
          </div>

          {record?.coordinator ? (
            <p className="text-caption text-secondary">
              {t('content.projects.currentCoordinator', { name: record.coordinator.name })}
            </p>
          ) : null}
        </div>

        <div className="space-y-4">
          <PublishingSidebar
            resource="projects"
            status={values.status}
            onStatusChange={(status) => setValue('status', status, { shouldDirty: true })}
            publishedAt={values.published_at}
            onPublishedAtChange={(next) => setValue('published_at', next, { shouldDirty: true })}
            updatedAt={record?.updated_at}
            updatedBy={record?.updated_by}
            pendingReview={record?.pending_review}
            saving={isSubmitting}
          />

          <TranslationProgress
            fields={{
              title: { label: t('content.fields.title'), value: values.title },
              summary: { label: t('content.fields.summary'), value: values.summary },
              body: { label: t('content.fields.body'), value: values.body },
            }}
          />
        </div>
      </div>

      <ConfirmDialog
        open={guard.blocking}
        title={t('editor.unsaved.title')}
        body={t('editor.unsaved.body')}
        confirmLabel={t('editor.unsaved.discard')}
        reversible={false}
        onCancel={guard.cancel}
        onConfirm={guard.proceed}
      />
    </form>
  );
}

function empty(): Fields {
  return {
    name: '',
    title: {},
    slug: {},
    summary: {},
    body: {},
    funder: {},
    partners: {},
    programme: '',
    starts_on: '',
    ends_on: '',
    budget: '',
    coordinator_staff_id: '',
    logo_media_id: null,
    logo: null,
    cover_media_id: null,
    cover: null,
    website_url: '',
    sort_order: 0,
    status: 'draft',
    published_at: null,
    pending_review: false,
  };
}

function toFields(row: ProjectRow): Fields {
  return {
    name: row.name,
    title: row.title,
    slug: row.slug,
    summary: row.summary,
    body: row.body,
    funder: row.funder,
    partners: row.partners,
    programme: row.programme ?? '',
    starts_on: row.starts_on ?? '',
    ends_on: row.ends_on ?? '',
    budget: row.budget === null ? '' : String(row.budget),
    coordinator_staff_id: row.coordinator_staff_id === null ? '' : String(row.coordinator_staff_id),
    logo_media_id: row.logo_media_id ?? null,
    logo: row.logo ?? null,
    cover_media_id: row.cover_media_id ?? null,
    cover: row.cover ?? null,
    website_url: row.website_url ?? '',
    sort_order: row.sort_order,
    status: row.status,
    published_at: row.published_at,
    pending_review: row.pending_review,
  };
}

function toInput(form: Fields, mayPublish: boolean): ProjectInput {
  return {
    name: form.name.trim(),
    title: form.title,
    slug: form.slug,
    summary: form.summary,
    body: form.body,
    funder: form.funder,
    partners: listToInput(form.partners),
    programme: form.programme.trim() === '' ? null : form.programme.trim(),
    starts_on: form.starts_on === '' ? null : form.starts_on,
    ends_on: form.ends_on === '' ? null : form.ends_on,
    budget: form.budget === '' ? null : Number(form.budget),
    coordinator_staff_id:
      form.coordinator_staff_id === '' ? null : Number(form.coordinator_staff_id),
    logo_media_id: form.logo_media_id,
    cover_media_id: form.cover_media_id,
    website_url: form.website_url.trim() === '' ? null : form.website_url.trim(),
    sort_order: Number(form.sort_order),
    status: form.status,
    published_at: form.published_at,
    pending_review: mayPublish ? form.pending_review : true,
  };
}
